import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { CartType } from '@/types/cart'
import { getErrorMessage } from '@/lib/utils'
import { fetchCart, addCartItem, updateCartItem, removeCartItem, clearCart } from '@/lib/api'
import { AddCartItemParams, UpdateCartItemParams } from '@/types/cart'

type CartStateType = {
  isLoading: boolean
  response: CartType | null
  error: {
    status?: number
    message: string
  } | null
}

type CartStoreType = {
  cartState: CartStateType
  getCart: () => Promise<CartType>
  addItem: (payload: AddCartItemParams) => Promise<CartType>
  updateItem: (productId: string, payload: UpdateCartItemParams) => Promise<CartType>
  removeItem: (productId: string) => Promise<CartType>
  clearCart: () => Promise<void>
  reset: () => void
}

const initialState: CartStateType = {
  isLoading: false,
  response: null,
  error: null
}

const useCartStore = create<CartStoreType>()(
  devtools((set, get) => ({
    cartState: initialState,
    getCart: async () => {
      const currentState = get().cartState
      if (currentState.isLoading) {
        return currentState.response!
      }

      set(() => ({
        cartState: {
          ...initialState,
          isLoading: true
        }
      }))

      try {
        const data = await fetchCart()

        set(() => ({
          cartState: {
            ...initialState,
            response: data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          cartState: {
            ...initialState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },
    addItem: async (payload: AddCartItemParams) => {
      const currentState = get().cartState

      set(() => ({
        cartState: {
          ...currentState,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await addCartItem(payload)

        set(() => ({
          cartState: {
            ...currentState,
            isLoading: false,
            response: data,
            error: null
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          cartState: {
            ...currentState,
            isLoading: false,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },
    updateItem: async (productId: string, payload: UpdateCartItemParams) => {
      const currentState = get().cartState

      set(() => ({
        cartState: {
          ...currentState,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await updateCartItem(productId, payload)

        set(() => ({
          cartState: {
            ...currentState,
            isLoading: false,
            response: data,
            error: null
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          cartState: {
            ...currentState,
            isLoading: false,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },
    removeItem: async (productId: string) => {
      const currentState = get().cartState

      set(() => ({
        cartState: {
          ...currentState,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await removeCartItem(productId)

        set(() => ({
          cartState: {
            ...currentState,
            isLoading: false,
            response: data,
            error: null
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          cartState: {
            ...currentState,
            isLoading: false,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },
    clearCart: async () => {
      const currentState = get().cartState

      set(() => ({
        cartState: {
          ...currentState,
          isLoading: true,
          error: null
        }
      }))

      try {
        await clearCart()

        set(() => ({
          cartState: {
            ...initialState,
            response: null
          }
        }))
      } catch (error: any) {
        set(() => ({
          cartState: {
            ...currentState,
            isLoading: false,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },
    reset: () => {
      set(() => ({
        cartState: initialState
      }))
    }
  }))
)

export default useCartStore
